/**
 * Transform raw query results into chart-specific data formats
 * 
 * This utility is shared across Dashboard and Admin (Reports Management) apps
 * to ensure consistent data transformation logic.
 * 
 * @param {Array<string>} columns - Column names from query result
 * @param {Array<Array>} rows - Row data from query result
 * @param {Object} config - Widget configuration object
 * @param {string} config.chartBy - Column to use for charting (what we're measuring)
 * @param {string} config.groupBy - Column to group by (categories/X-axis)
 * @param {string} config.xAxis - Alternative to chartBy for backward compatibility
 * @param {string} type - Chart type ('line', 'grouped-bar', 'stacked-bar', 'stacked-line', etc.)
 * @returns {Object|null} Transformed data structure or null if no valid data
 * 
 * @example
 * // Single series line chart
 * const result = transformChartData(
 *   ['category', 'value'],
 *   [['Jan', 100], ['Feb', 150]],
 *   { chartBy: 'category' },
 *   'line'
 * );
 * // Returns: { categories: ['Jan', 'Feb'], values: [100, 150] }
 * 
 * @example
 * // Multi-series grouped bar chart
 * const result = transformChartData(
 *   ['category', 'product', 'value'],
 *   [['Jan', 'A', 100], ['Jan', 'B', 150], ['Feb', 'A', 120]],
 *   { chartBy: 'product', groupBy: 'category' },
 *   'grouped-bar'
 * );
 * // Returns: { categories: ['Jan', 'Feb'], series: [{ name: 'A', data: [100, 120] }, { name: 'B', data: [150, 0] }] }
 */
/** SQL month bucket YYYY-MM → "Mar 2025" for chart X-axis; other strings unchanged. */
const formatYearMonthCategoryLabel = (raw) => {
  const s = String(raw ?? '');
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (!m) return s;
  const y = parseInt(m[1], 10);
  const monthIndex = parseInt(m[2], 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) return s;
  const d = new Date(Date.UTC(y, monthIndex, 1));
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

// Helper to strip table prefix from column name (e.g., "agents.name" -> "name")
const stripTablePrefix = (columnName) => {
  if (!columnName) return columnName;
  return columnName.includes('.') ? columnName.split('.').pop() : columnName;
};

// Helper to find column index, handling table-prefixed names
const findColumnIndex = (columns, columnName) => {
  if (!columnName) return -1;
  // Try exact match first
  let index = columns.indexOf(columnName);
  if (index === -1) {
    // Try without table prefix
    const stripped = stripTablePrefix(columnName);
    index = columns.indexOf(stripped);
  }
  return index;
};

export const transformChartData = (columns, rows, widgetConfig, type) => {
  if (!rows || rows.length === 0) {
    return null;
  }

  // Extract config from dataSources (type-specific config is now inside dataSource.config)
  // Check for table or custom_query data sources
  const dataSources = widgetConfig?.dataSources || [];
  const relevantSource = dataSources.find((ds) => ds.type === 'table' || ds.type === 'custom_query');
  const config = relevantSource?.config || widgetConfig || {};

  switch (type) {
    case 'bar':
    case 'line': {
      // Widget-data API returns standardized columns: category/timestamp, value, entity.
      // For time series queries, the result uses 'category' or 'timestamp' column.
      let categoryIndex = columns.indexOf('category');
      const valueIndex = columns.indexOf('value');

      // Support 'timestamp' column for table-based line charts
      // Only set isTimestamp=true when we actually find a 'timestamp' column
      let isTimestamp = false;
      if (categoryIndex === -1) {
        categoryIndex = columns.indexOf('timestamp');
        isTimestamp = categoryIndex !== -1;
      }

      // Helper to format timestamp for display
      const formatCategory = (value) => {
        if (isTimestamp && value) {
          try {
            const date = new Date(value);
            if (!Number.isNaN(date.getTime())) {
              // Format as "MMM DD HH:mm" (e.g., "Jan 21 11:22")
              return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              });
            }
          } catch {
            // Fall through to string conversion
          }
        }
        return String(value);
      };

      if (categoryIndex !== -1 && valueIndex !== -1) {
        // Check for entity column (for multi-line charts per entity)
        const entityIndex = columns.indexOf('entity');

        // Helper to parse value (preserve strings for categorical Y-axis)
        const parseValue = (val) => {
          const num = parseFloat(val);
          return Number.isNaN(num) ? val : num;
        };

        // Multi-series by entity (one line per entity, e.g., host, service)
        if (entityIndex !== -1) {
          const categoriesSet = new Set();
          const seriesMap = new Map();

          rows.forEach(row => {
            const category = formatCategory(row[categoryIndex]);
            const entityName = String(row[entityIndex]);
            const value = parseValue(row[valueIndex]);

            categoriesSet.add(category);

            if (!seriesMap.has(entityName)) {
              seriesMap.set(entityName, {});
            }
            seriesMap.get(entityName)[category] = value;
          });

          const categories = Array.from(categoriesSet);
          const series = Array.from(seriesMap.entries()).map(([name, dataMap]) => ({
            name,
            data: categories.map(cat => dataMap[cat] !== undefined ? dataMap[cat] : null)
          }));

          return { categories, series };
        }

        // Check if this is a 2D result (has both chartBy and groupBy creating multiple series)
        // This happens when config has chartBy (what we're measuring) and groupBy (categories/X-axis)
        // AND the query groups by TWO columns, creating a series per chartBy value

        // If we have chartBy in the columns, this is a multi-series result
        const chartByIndex = config.chartBy ? findColumnIndex(columns, config.chartBy) : -1;

        if (chartByIndex !== -1 && chartByIndex !== categoryIndex) {
          // Multi-series (one line/bar per chartBy value, grouped by category)
          const categoriesSet = new Set();
          const seriesMap = new Map();

          rows.forEach(row => {
            const rawCategory = String(row[categoryIndex]);
            const seriesName = String(row[chartByIndex]);
            const value = parseFloat(row[valueIndex]) || 0;

            categoriesSet.add(rawCategory);

            if (!seriesMap.has(seriesName)) {
              seriesMap.set(seriesName, {});
            }
            seriesMap.get(seriesName)[rawCategory] = value;
          });

          const rawCategories = Array.from(categoriesSet);
          const series = Array.from(seriesMap.entries()).map(([name, dataMap]) => ({
            name,
            data: rawCategories.map(cat => dataMap[cat] || 0)
          }));

          const categories = rawCategories.map((cat) =>
            formatYearMonthCategoryLabel(cat)
          );

          return { categories, series };
        }

        // Single series
        // Preserve string values (like "OK", "UP") for categorical Y-axis
        return {
          categories: rows.map(row => formatCategory(row[categoryIndex])),
          values: rows.map(row => parseValue(row[valueIndex]))
        };
      }

      // Fallback to old logic for backward compatibility
      const fallbackChartByIndex = findColumnIndex(columns, config.chartBy || config.xAxis);
      if (fallbackChartByIndex !== -1 && valueIndex !== -1) {
        return {
          categories: rows.map(row => formatCategory(row[fallbackChartByIndex])),
          values: rows.map(row => parseFloat(row[valueIndex]) || 0)
        };
      }

      return null;
    }

    case 'line-2d': {
      const line2dChartByIndex = findColumnIndex(columns, config.chartBy || config.xAxis);
      const line2dValueIndex = columns.indexOf('value');

      if (line2dChartByIndex !== -1 && line2dValueIndex !== -1) {
        return {
          categories: rows.map(row => String(row[line2dChartByIndex])),
          values: rows.map(row => parseFloat(row[line2dValueIndex]) || 0)
        };
      }
      return null;
    }

    case 'stacked-bar':
    case 'stacked-line':
    case 'grouped-bar': {
      const stackedChartByIndex = findColumnIndex(columns, config.chartBy || config.xAxis);
      const stackedGroupByIndex = findColumnIndex(columns, config.groupBy);
      const stackedValueIndex = columns.indexOf('value');

      if (stackedChartByIndex !== -1 && stackedGroupByIndex !== -1 && stackedValueIndex !== -1) {
        const categoriesSet = new Set();
        const groupsSet = new Set();
        const dataMap = {};

        rows.forEach(row => {
          const category = String(row[stackedChartByIndex]);
          const group = String(row[stackedGroupByIndex]);
          const value = parseFloat(row[stackedValueIndex]) || 0;

          categoriesSet.add(category);
          groupsSet.add(group);

          const key = `${group}_${category}`;
          dataMap[key] = value;
        });

        const rawCategories = Array.from(categoriesSet);
        const groups = Array.from(groupsSet);

        const series = groups.map(group => {
          const data = rawCategories.map(category => {
            const key = `${group}_${category}`;
            return dataMap[key] || 0;
          });
          return { name: group, data };
        });

        const categories = rawCategories.map(formatYearMonthCategoryLabel);

        return { categories, series };
      }
      return null;
    }

    default:
      return null;
  }
};

